import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { parseISO } from 'date-fns';
import { AlertCircle } from 'lucide-react';
import OverviewDashboardHeader from './OverviewDashboardHeader';
import KpiCards from './KpiCards';
import LabourCostLineChart from './LabourCostLineChart';
import LocationDonutChart from './LocationDonutChart';
import DepartmentBarChart from './DepartmentBarChart';
import ReportsNeedingAttention from './ReportsNeedingAttention';
import LeaveAndPayrollSection from './LeaveAndPayrollSection';
import ComplianceAlerts from './ComplianceAlerts';
import EmployeeSpotlight from './EmployeeSpotlight';
import IndividualEmployeeReport from './IndividualEmployeeReport';
// Progressive independent-settle hook (default).
// To revert to the previous all-or-nothing Promise.all behaviour:
//   import { useOverviewDashboardData, useOverviewWeekNavigation } from './useOverviewDashboardData.sequential';
import { useOverviewDashboardData, useOverviewWeekNavigation } from './useOverviewDashboardData';
import { exportOverviewDashboardCsv } from './exportOverviewDashboard';

const OverviewDashboard: React.FC = () => {
  const navigate = useNavigate();
  const weekNav = useOverviewWeekNavigation();
  const {
    data,
    sectionLoading,
    error,
    refresh,
    weekStart,
    weekEnd,
    weekStartsOn,
    summary,
    holidayRequests,
    employeeDirectory,
  } = useOverviewDashboardData(weekNav.currentWeek);
  const [refreshing, setRefreshing] = useState(false);
  const [spotlightRefreshNonce, setSpotlightRefreshNonce] = useState(0);

  const weekLoading = sectionLoading.week;
  const summaryLoading = sectionLoading.summary;
  const leaveLoading = sectionLoading.holidays;
  const payrollLoading = sectionLoading.week;

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      setSpotlightRefreshNonce((n) => n + 1);
      await refresh();
    } finally {
      setRefreshing(false);
    }
  }, [refresh]);

  const handleExport = useCallback(() => {
    if (data) exportOverviewDashboardCsv(data);
  }, [data]);

  const handleWeekSelect = useCallback(
    (weekStartYmd: string) => {
      weekNav.setCurrentWeek(parseISO(weekStartYmd));
    },
    [weekNav],
  );

  return (
    <div className="flex-1 overflow-y-auto bg-[#F4F7F9] min-h-0">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-6 space-y-6">
        <OverviewDashboardHeader
          weekStart={weekStart}
          weekEnd={weekEnd}
          weekStartsOn={weekStartsOn}
          currentWeek={weekNav.currentWeek}
          onPreviousWeek={weekNav.goToPreviousWeek}
          onNextWeek={weekNav.goToNextWeek}
          onCurrentWeek={weekNav.goToCurrentWeek}
          onWeekSelect={handleWeekSelect}
          onExport={handleExport}
          onRefresh={handleRefresh}
          refreshing={refreshing}
          // Keep week nav usable while tiles load independently
          loading={false}
          onCustomReport={() => navigate('/reports')}
        />

        {error && (
          <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            {error}
            <button
              type="button"
              onClick={() => void handleRefresh()}
              className="ml-auto text-red-800 underline hover:no-underline"
            >
              Retry
            </button>
          </div>
        )}

        <KpiCards cards={data?.kpiCards ?? []} loading={weekLoading} />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-stretch">
          <div className="lg:col-span-6 flex">
            <LabourCostLineChart data={data?.labourCostChart ?? []} loading={weekLoading} />
          </div>
          <div className="lg:col-span-3 flex">
            <LocationDonutChart
              items={data?.locationCosts ?? []}
              total={data?.totalLabourCost ?? 0}
              loading={weekLoading}
            />
          </div>
          <div className="lg:col-span-3 flex">
            <DepartmentBarChart items={data?.departmentCosts ?? []} loading={weekLoading} />
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <ReportsNeedingAttention
            items={data?.reportsNeedingAttention ?? []}
            loading={summaryLoading}
          />
          <LeaveAndPayrollSection
            leaveAbsence={data?.leaveAbsence ?? []}
            payrollReadiness={
              data?.payrollReadiness ?? { percent: 0, approved: 0, pending: 0, rejected: 0, total: 0 }
            }
            leaveLoading={leaveLoading}
            payrollLoading={payrollLoading}
          />
          <ComplianceAlerts items={data?.complianceAlerts ?? []} loading={summaryLoading} />
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 items-stretch">
          <div className="xl:col-span-2 min-h-0 h-full">
            <EmployeeSpotlight
              dashboardWeek={weekNav.currentWeek}
              weekStartsOn={weekStartsOn}
              loading={false}
              metricsReady={!weekLoading}
              refreshNonce={spotlightRefreshNonce}
            />
          </div>
          <div className="min-h-0 h-full">
            <IndividualEmployeeReport
              dashboardWeek={weekNav.currentWeek}
              weekStartsOn={weekStartsOn}
              holidayRequests={holidayRequests}
              summary={summary}
              employeeDirectory={employeeDirectory}
              seedReport={null}
              loading={false}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default OverviewDashboard;
